import ChangePasswordDialog from '@/components/dialog/ChangePasswordDialog'
import { useNavigationGuard } from '@/components/navigation/navigation-guard-context'
import { OverflowText } from '@/components/ui/overflow-text'
import { UserAvatar } from '@/components/ui/user-avatar'
import { useSidebar } from '@/hooks/useSidebar'
import { useTheme } from '@/hooks/useTheme'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/useAuthStore'
import { useThemeStore } from '@/store/useThemeStore'
import { KeyRound, LogOut, Moon, MoreVertical, Sun, TriangleAlert } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useEffect, useState, type ReactNode } from 'react'
import { Button } from '../ui/button'
import MinimizeMenuIcon from '@/assets/icons/minimize_menu.svg'

function getUserDisplayName(user: ReturnType<typeof useAuthStore.getState>['user']): string {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
  return fullName || user?.username || user?.email || 'User'
}

function getUserInitials(displayName: string): string {
  return (
    displayName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('') || 'U'
  ).toUpperCase()
}

function SidebarUserProfile({ collapsed }: { collapsed: boolean }) {
  const { requestNavigation } = useNavigationGuard()
  const [open, setOpen] = useState(false)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const user = useAuthStore((state) => state.user)
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(displayName)
  const router = useRouter()
  const logout = useAuthStore((state) => state.logout)
  const { isDark, toggleTheme } = useThemeStore()
  const t = useTranslations('sidebar')

  const handleLogout = () => {
    requestNavigation(() => {
      logout()
      router.replace('/login')
    })
  }

  if (collapsed) {
    return (
      <div className="flex justify-center">
        <UserAvatar initials={initials} className="size-10" />
      </div>
    )
  }

  return (
    <>
      <div className="relative flex items-center gap-3">
        <UserAvatar initials={initials} className="size-10" />
        <div className="flex min-w-0 flex-1 flex-col">
          <OverflowText className="text-main-text-lv01 text-comp-button-bt1 leading-comp-button-bt1 font-semibold">
            {displayName}
          </OverflowText>
          <span className="truncate text-main-text-lv03 text-ui-caption-c2 leading-ui-caption-c2">
            {user?.group_name?.name}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="rounded-md p-1 hover:bg-black/5 text-secondary-500"
          aria-label={t('userMenu')}
        >
          <MoreVertical className="size-4" />
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
            <div
              className="absolute right-2 bottom-14 z-20 w-52 overflow-hidden rounded-xl bg-white"
              style={{ boxShadow: '0 8px 24px rgba(17, 21, 25, 0.12)' }}
            >
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-secondary-50 text-secondary-800"
                onClick={() => {
                  toggleTheme()
                  setOpen(false)
                }}
              >
                {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
                {isDark ? t('lightMode') : t('darkMode')}
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-secondary-50 text-secondary-800"
                onClick={() => setOpen(false)}
              >
                <TriangleAlert className="size-4" /> {t('reportProblem')}
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-secondary-50 text-secondary-800"
                onClick={() => {
                  setOpen(false)
                  setChangePasswordOpen(true)
                }}
              >
                <KeyRound className="size-4" /> {t('changePassword')}
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-secondary-50 text-secondary-800"
                onClick={handleLogout}
              >
                <LogOut className="size-4" /> {t('logout')}
              </button>
            </div>
          </>
        )}
      </div>
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </>
  )
}

interface AppSidebarProps {
  subtitle: string
  beforeDivider?: (collapsed: boolean) => ReactNode
  children: (collapsed: boolean, setCollapsed: (collapsed: boolean) => void) => ReactNode
}

export function AppSidebar({ subtitle, beforeDivider, children }: AppSidebarProps) {
  const { adminSidebarLogo, adminSidebarBrandName } = useTheme()
  const [collapsed, setCollapsed] = useState(false)
  const [showBackdrop, setShowBackdrop] = useState(false)
  const [backdropVisible, setBackdropVisible] = useState(false)
  const { isMobile, open, setOpen } = useSidebar()
  const effectiveCollapsed = isMobile ? false : collapsed
  const sidebarWidth = effectiveCollapsed ? 92 : 272
  const tSidebar = useTranslations('sidebar')

  useEffect(() => {
    if (open && isMobile) {
      setShowBackdrop(true)
      const frame = window.requestAnimationFrame(() => setBackdropVisible(true))

      return () => {
        window.cancelAnimationFrame(frame)
      }
    }

    setBackdropVisible(false)
    const timeout = window.setTimeout(() => setShowBackdrop(false), 200)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [isMobile, open])

  return (
    <>
      {showBackdrop && isMobile && (
        <button
          type="button"
          aria-label={tSidebar('closeSidebar')}
          className={cn(
            'fixed inset-0 z-30 bg-black/20 transition-opacity duration-200 ease-in-out motion-reduce:transition-none md:hidden',
            backdropVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        className={cn(
          'fixed top-0 left-0 z-40 flex h-screen shrink-0 transform-gpu flex-col gap-4 overflow-hidden border-r p-4 pr-3.75 transition-[width,min-width,transform] duration-300 ease-in-out motion-reduce:transition-none md:sticky md:z-auto bg-main-surface-lv-01',
          open ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
        style={{
          width: sidebarWidth,
          minWidth: sidebarWidth,
        }}
      >
        <div className="flex flex-col gap-2">
          <div className={cn('flex items-center gap-2', effectiveCollapsed ? 'justify-center' : 'justify-between')}>
            {!effectiveCollapsed && (
              <div className="flex items-center gap-2 animate-in fade-in duration-300 motion-reduce:animate-none">
                {adminSidebarLogo && (
                  <img
                    src={adminSidebarLogo}
                    alt={adminSidebarBrandName}
                    className="size-14.5 shrink-0 rounded-[10px] object-cover"
                  />
                )}
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-[18px] font-bold leading-tight text-main-text-lv01">
                    {adminSidebarBrandName}
                  </span>
                </div>
              </div>
            )}
            <Button
              type="button"
              onClick={() => (isMobile ? setOpen(false) : setCollapsed((value) => !value))}
              size="sm"
              variant="ghost"
              aria-label={isMobile ? tSidebar('closeSidebar') : tSidebar('toggleSidebar')}
            >
              <MinimizeMenuIcon />
            </Button>
          </div>

          {!effectiveCollapsed && (
            <span className="truncate text-ui-caption-c2 leading-ui-caption-c2 text-main-text-lv03">{subtitle}</span>
          )}
        </div>

        {beforeDivider?.(effectiveCollapsed)}
        <div className="h-px w-full bg-main-border-lv03" />

        <div className={cn('flex-1', effectiveCollapsed ? 'overflow-y-hidden' : 'overflow-y-auto')}>
          {children(effectiveCollapsed, setCollapsed)}
        </div>

        <div className="border-t border-t-main-border-lv03 p-2 pt-3.75">
          <SidebarUserProfile collapsed={effectiveCollapsed} />
        </div>
      </aside>
    </>
  )
}
